import mediaManagerTemplateDataMapper from 'modules/core/domain/data-mappers/media-manager-template-data-mapper';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import {
  COPY_MEDIA_MANAGER_TEMPLATE_MUTATION,
  CREATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
  DELETE_MEDIA_MANAGER_TEMPLATE_MUTATION,
  MEDIA_MANAGER_PRESETS_QUERY,
  MEDIA_MANAGER_TEMPLATES_QUERY,
  MEDIA_MANAGER_TEMPLATE_QUERY,
  MediaManagerTemplateGql,
  UPDATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
} from 'modules/manager/domain/graphql/media-manager-templates';
import {
  CopyMediaManagerTemplate,
  CreateMediaManagerTemplate,
  UpdateMediaManagerTemplate,
} from 'modules/core/domain/interfaces/supabase/media-manager/actions';
import { baseApi } from 'shared/api/base-api';
import type { Draft } from 'immer';
import type { AppDispatch, RootState } from 'store/store';

export const MEDIA_MANAGER_TEMPLATE_TAG = 'MediaManagerTemplate' as const;
export const MEDIA_MANAGER_TEMPLATES_LIST_TAG = { type: MEDIA_MANAGER_TEMPLATE_TAG, id: 'ORG-LIST' };
export const MEDIA_MANAGER_PRESETS_LIST_TAG = { type: MEDIA_MANAGER_TEMPLATE_TAG, id: 'PRESET-LIST' };

function mapTemplates(rows: MediaManagerTemplateGql[] | null | undefined): MediaManagerTemplate[] {
  return (rows ?? []).map(mediaManagerTemplateDataMapper);
}

interface TemplatesQueryArgs {
  tournamentId: number;
  sportType?: string;
}

export const mediaManagerApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    mediaManagerTemplates: build.query<MediaManagerTemplate[], TemplatesQueryArgs>({
      query: ({ tournamentId, sportType }) => ({
        query: MEDIA_MANAGER_TEMPLATES_QUERY,
        variables: { tournamentId, sportType },
      }),
      transformResponse: (raw: { mediaManagerTemplates?: MediaManagerTemplateGql[] }) => mapTemplates(raw.mediaManagerTemplates),
      providesTags: (result) => [
        MEDIA_MANAGER_TEMPLATES_LIST_TAG,
        ...(result ?? []).map((template) => ({ type: MEDIA_MANAGER_TEMPLATE_TAG, id: template.id })),
      ],
    }),
    mediaManagerPresets: build.query<MediaManagerTemplate[], { sportType?: string } | void>({
      query: (arg) => ({
        query: MEDIA_MANAGER_PRESETS_QUERY,
        variables: { sportType: arg && 'sportType' in arg ? arg.sportType : undefined },
      }),
      transformResponse: (raw: { mediaManagerPresets?: MediaManagerTemplateGql[] }) => mapTemplates(raw.mediaManagerPresets),
      providesTags: (result) => [
        MEDIA_MANAGER_PRESETS_LIST_TAG,
        ...(result ?? []).map((template) => ({ type: MEDIA_MANAGER_TEMPLATE_TAG, id: template.id })),
      ],
    }),
    mediaManagerTemplate: build.query<MediaManagerTemplate, number>({
      query: (id) => ({
        query: MEDIA_MANAGER_TEMPLATE_QUERY,
        variables: { id },
      }),
      transformResponse: (raw: { mediaManagerTemplate: MediaManagerTemplateGql }) =>
        mediaManagerTemplateDataMapper(raw.mediaManagerTemplate),
      providesTags: (_result, _error, id) => [{ type: MEDIA_MANAGER_TEMPLATE_TAG, id }],
    }),
    createMediaManagerTemplate: build.mutation<MediaManagerTemplate, CreateMediaManagerTemplate>({
      query: (input) => ({
        query: CREATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { createMediaManagerTemplate: MediaManagerTemplateGql }) =>
        mediaManagerTemplateDataMapper(raw.createMediaManagerTemplate),
      invalidatesTags: (_result, _error, input) =>
        input.tournamentId == null ? [MEDIA_MANAGER_PRESETS_LIST_TAG] : [MEDIA_MANAGER_TEMPLATES_LIST_TAG],
    }),
    updateMediaManagerTemplate: build.mutation<MediaManagerTemplate, UpdateMediaManagerTemplate>({
      query: (input) => ({
        query: UPDATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { updateMediaManagerTemplate: MediaManagerTemplateGql }) =>
        mediaManagerTemplateDataMapper(raw.updateMediaManagerTemplate),
      invalidatesTags: (_result, _error, input) => [
        { type: MEDIA_MANAGER_TEMPLATE_TAG, id: input.id },
        MEDIA_MANAGER_TEMPLATES_LIST_TAG,
        MEDIA_MANAGER_PRESETS_LIST_TAG,
      ],
    }),
    deleteMediaManagerTemplate: build.mutation<{ status: string }, number>({
      query: (id) => ({
        query: DELETE_MEDIA_MANAGER_TEMPLATE_MUTATION,
        variables: { id },
      }),
      transformResponse: (raw: { deleteMediaManagerTemplate: { status: string } }) => raw.deleteMediaManagerTemplate,
      invalidatesTags: (_result, _error, id) => [
        { type: MEDIA_MANAGER_TEMPLATE_TAG, id },
        MEDIA_MANAGER_TEMPLATES_LIST_TAG,
        MEDIA_MANAGER_PRESETS_LIST_TAG,
      ],
    }),
    copyMediaManagerTemplate: build.mutation<MediaManagerTemplate, CopyMediaManagerTemplate>({
      query: (input) => ({
        query: COPY_MEDIA_MANAGER_TEMPLATE_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { copyMediaManagerTemplate: MediaManagerTemplateGql }) =>
        mediaManagerTemplateDataMapper(raw.copyMediaManagerTemplate),
      // No `invalidatesTags` on purpose: the mutation returns the whole copy, so the
      // list cache is patched with it (`upsertMediaManagerTemplateIntoCache`) instead
      // of paying a second round-trip for data we already hold.
    }),
  }),
  overrideExisting: false,
});

/**
 * Puts a freshly created copy into every cached templates list it belongs to, so the
 * list shows it without a page reload and without an extra request. A copy always
 * lands in the tournament it was made for; a list filtered by sport takes it only
 * when the sport matches.
 */
export const upsertMediaManagerTemplateIntoCache =
  (copy: MediaManagerTemplate) => (dispatch: AppDispatch, getState: () => RootState) => {
    // Every cached list has to be patched, not just the one currently on screen:
    // the same list is cached once per `sportType` filter it was opened with.
    Object.values(getState().api.queries).forEach((entry) => {
      if (entry?.endpointName !== 'mediaManagerTemplates') return;

      const args = entry.originalArgs as TemplatesQueryArgs;
      if (args.tournamentId !== copy.tournamentId) return;
      if (args.sportType && args.sportType !== copy.sportType) return;

      dispatch(
        mediaManagerApi.util.updateQueryData('mediaManagerTemplates', args, (draft) => {
          if (draft.some((template) => template.id === copy.id)) return;
          // The cast is immer noise, not a type hole: `objects` holds fabric objects
          // full of DOM types, and their `Draft<>` projection never matches the original.
          draft.push(copy as MediaManagerTemplate & Draft<MediaManagerTemplate>);
        }),
      );
    });
  };

export const {
  useMediaManagerTemplatesQuery,
  useMediaManagerPresetsQuery,
  useLazyMediaManagerTemplateQuery,
  useCreateMediaManagerTemplateMutation,
  useUpdateMediaManagerTemplateMutation,
  useDeleteMediaManagerTemplateMutation,
  useCopyMediaManagerTemplateMutation,
} = mediaManagerApi;
